import { useEffect, useRef } from 'react'

/**
 * 浮层栈 —— 按 Esc 只关**最上面那一层**。
 *
 * 为什么必须共享一份栈（2026-10-09）：
 *   `Modal` / `Drawer` / 放大灯箱各自往 `window` 上挂了一个 keydown 监听，
 *   谁都不知道对方存在。同一个界面里两层同时打开时，按一次 Esc 会
 *   **两个一起关**。触发它的现实场景就是这一轮新增的：
 *   AI 模特库的「生成新模特」抽屉 → 里面点「生成 AI 模特」→ 弹出价格确认，
 *   此时按 Esc 想取消这次生成，结果连**填了一半的抽屉**一起没了。
 *
 * 判据是「后压进栈的那一个」：谁后打开谁响应 —— 与 DOM 层叠顺序天然一致
 * （见 global.css 里那套 z-index 尺子：遮罩 60 / 抽屉与弹窗 61 / 大图 90）。
 *
 * ⚠️ `onClose` 走 ref、**不进依赖数组**：它是每次渲染都会新建的函数，
 *    进了依赖就会让 effect 反复卸载重挂，而重挂会把 token 重新压到栈顶 ——
 *    于是「抽屉自己重渲染一次」就能把栈顶从弹窗手里抢回抽屉，
 *    下一个 Esc 又变成关抽屉。这个 bug 只在两层同时打开时偶发，极难查。
 *
 * 与 `useScrollLock` 的分工：那个管滚动、这个管键盘。两者都是「弹层之间
 * 不许各写一遍」的同一类收敛。
 */
const stack: symbol[] = []

export function useEscapeLayer(onClose: () => void, active: boolean) {
  // 每次渲染后同步最新的回调；effect 本身不依赖它（理由见上）
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    if (!active) return
    const token = Symbol('overlay-layer')
    stack.push(token)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // 上面还有一层 —— 这次 Esc 归它，别顺手把底下这层也关了
      if (stack[stack.length - 1] !== token) return
      closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      const i = stack.indexOf(token)
      if (i >= 0) stack.splice(i, 1)
      window.removeEventListener('keydown', onKey)
    }
  }, [active])
}
